import React, { useState } from 'react';
import { SearchResult } from '../../contexts/SearchResultsContext';
import MarkdownResult from './results/MarkdownResult';
import CodeResult from './results/CodeResult';
import LinkResult from './results/LinkResult';
import ImageResult from './results/ImageResult';
import TableResult from './results/TableResult';
import VideoResult from './results/VideoResult';
import InfoboxResult from './results/InfoboxResult';
import ActionResult from './results/ActionResult';
import Modal from '../../../components/Modal';
import { IconX } from '@tabler/icons-react';
import { cn } from '../../../utils/cn';
import ImageWidgetResult from './results/ImageWidgetResult';
import Fallback from './results/Fallback';

interface ResultCardProps {
  result: SearchResult;
  onTrack?: (type: string) => void;
}

const ResultCard: React.FC<ResultCardProps> = ({ result, onTrack }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderContent = () => {
    if (result.loading) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-[var(--rhea-card-bg)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--rhea-accent-primary)] border-t-transparent"></div>
        </div>
      );
    }
    switch (result.type) {
      case 'markdown':
        return <MarkdownResult content={result.data.content} />;
      case 'code':
        return <CodeResult code={result.data.code} language={result.data.language} />;
      case 'link':
        return (
          <LinkResult
            url={result.data.url}
            title={result.data.title}
            description={result.data.description}
            icon={result.data.icon}
            thumbnail={result.data.thumbnail}
          />
        );
      case 'image':
        return (
          <ImageResult
            url={result.data.url}
            alt={result.data.alt}
            caption={result.data.caption}
          />
        );
      case 'video':
        return <VideoResult url={result.data.url} title={result.data.title} description={result.data.description} />;
      case 'infobox':
        return <InfoboxResult title={result.data.title} description={result.data.description} attributes={result.data.attributes} category={result.data.category} />;
      case 'table':
        return <TableResult headers={result.data?.headers} rows={result.data?.rows} />;
      case 'action':
        return <ActionResult action={result.data} />;
      case 'image_results':
        return <ImageWidgetResult images={result.data?.images} title={result.data?.title || `Images for: ${result.data?.query}`} />;
      default:
        return (
          <Fallback
              title={result?.data?.title || ''}
              description={result?.data?.description || ''}
              url={result?.data?.url || ''}
              image_urls={result?.data?.image_urls || []}
              type={result?.data?.type || 'generic'}
              result={result.data || result}
            />
        );
    }
  };

  // Handle click for link-type results or custom onClick
  const handleClick = () => {
    // Track the interaction for all result types
    if (onTrack) {
      onTrack(result.type);
    }
    
    if (result.type === 'link') {
      window.open(result.data.url, '_blank', 'noopener,noreferrer');
    } else {
      setIsModalOpen(true);
    }
  };

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center bg-gray-700 hover:bg-gray-600 rounded-full transition-colors duration-200 z-10 text-gray-300 hover:text-white"
            aria-label="Close modal"
          >
            <IconX size={16} />
        </button>
        <div className="bg-gray-800 text-white border border-gray-700 rounded-lg shadow-xl max-w-[90vw] max-h-[90vh] min-w-[50vw] min-h-[50vh] overflow-auto flex">
          {renderContent()}
        </div>
      </Modal>
      <div 
        className="w-full h-full cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-md"
        onClick={handleClick}
      >
        <div className="w-full h-full overflow-hidden">
          {renderContent()}
        </div>
        
      </div>
    </>
  );
};

export default ResultCard; 